import { supabaseRestRequest } from './familyService';
import { getCurrentUserId, type SOSDisease } from './sosService';
import { supabase } from '../lib/supabase';

export interface SOSConsultationInput {
  disease: SOSDisease;
  symptomsSelected: Record<string, string | string[]>;
  observations?: string;
  familyMemberId?: string | null;
  recommendedRemedyName?: string | null;
  processingTimeMs?: number | null;
}

export interface SOSConsultationRecord {
  id: string;
  user_id: string;
  family_member_id: string | null;
  disease_id: string;
  disease_name: string;
  symptoms_selected: Record<string, string>;
  observations: string | null;
  recommended_remedy_name: string | null;
  processing_time_ms: number | null;
  created_at: string;
}

interface SosFunctionResponse {
  recommendedRemedyName?: string;
  geminiRawText?: string;
  processingTimeMs?: number;
  error?: string;
  stage?: string;
  promptLength?: number;
  consultation?: {
    id: string;
    user_id: string;
    family_member_id: string | null;
    disease_id: string;
    disease_name: string;
    symptoms_selected: Record<string, string>;
    observations: string | null;
    recommended_remedy_name: string | null;
    processing_time_ms: number | null;
    created_at: string;
  };
}

class SOSConsultationService {
  private static instance: SOSConsultationService;

  private constructor() {}

  static getInstance(): SOSConsultationService {
    if (!SOSConsultationService.instance) {
      SOSConsultationService.instance = new SOSConsultationService();
    }
    return SOSConsultationService.instance;
  }

  async createConsultation(input: SOSConsultationInput): Promise<SOSConsultationRecord> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('Usuário não autenticado. Faça login novamente.');
    }

    console.log('🔵 [sosConsultationService] Chamando Edge Function sos-ai-agent...');
    console.log('📦 Payload:', {
      diseaseId: input.disease.id,
      symptomsSelected: input.symptomsSelected,
      observations: input.observations ?? null,
      familyMemberId: input.familyMemberId ?? null
    });

    const { data, error } = await supabase.functions.invoke<SosFunctionResponse>('sos-ai-agent', {
      body: {
        diseaseId: input.disease.id,
        symptomsSelected: input.symptomsSelected,
        observations: input.observations ?? null,
        familyMemberId: input.familyMemberId ?? null
      }
    });

    console.log('📥 Resposta da Edge Function:', { data, error });

    if (error) {
      console.error('❌ [sosConsultationService] Erro na Edge Function sos-ai-agent:', error);
      throw new Error(error.message || 'Erro ao processar a consulta SOS. Tente novamente.');
    }

    if (data?.error) {
      const stage = 'stage' in data ? ` (fase: ${String((data as { stage?: string }).stage)})` : '';
      throw new Error(`${data.error}${stage}`);
    }

    if (!data?.consultation) {
      throw new Error('Resposta inválida da Edge Function.');
    }

    const record = data.consultation;

    return {
      id: record.id,
      user_id: record.user_id,
      family_member_id: record.family_member_id,
      disease_id: record.disease_id,
      disease_name: record.disease_name,
      symptoms_selected: record.symptoms_selected ?? {},
      observations: record.observations,
      recommended_remedy_name: record.recommended_remedy_name,
      processing_time_ms: record.processing_time_ms ?? data.processingTimeMs ?? null,
      created_at: record.created_at,
    };
  }

  async listUserConsultations(): Promise<SOSConsultationRecord[]> {
    const userId = getCurrentUserId();
    const data = await supabaseRestRequest(
      `sos_consultations?user_id=eq.${userId}&order=created_at.desc&select=*`,
      'GET'
    );

    return (Array.isArray(data) ? data : []).map((record) => ({
      id: record.id,
      user_id: record.user_id,
      family_member_id: record.family_member_id,
      disease_id: record.disease_id,
      disease_name: record.disease_name,
      symptoms_selected: record.symptoms_selected ?? {},
      observations: record.observations,
      recommended_remedy_name: record.recommended_remedy_name,
      processing_time_ms: record.processing_time_ms,
      created_at: record.created_at,
    }));
  }
}

export const sosConsultationService = SOSConsultationService.getInstance();
